import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { Car, Sparkles, Home as HomeIcon, Shield, Clock, MapPin, Phone, ArrowRight, CheckCircle2, Star, Users, Award, X, Gift } from 'lucide-react';

const HomePage = () => {
  const [showPromo, setShowPromo] = useState(false);

  useEffect(() => {
    // Afficher le pop-up après 3 secondes si pas déjà vu
    const hasSeenPromo = sessionStorage.getItem('hasSeenPromo');
    if (!hasSeenPromo) {
      const timer = setTimeout(() => {
        setShowPromo(true);
        sessionStorage.setItem('hasSeenPromo', 'true');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, []);

  const services = [
    {
      id: 'auto-complet',
      name: 'Nettoyage Auto Complet',
      description: 'Intérieur + extérieur pour un véhicule impeccable',
      price: 'À partir de 70€',
      icon: Car,
      image: 'https://images.unsplash.com/photo-1761312834150-4beefff097a7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwxfHxjYXIlMjB3YXNoaW5nJTIwZm9hbSUyMGV4dGVyaW9yJTIwc29hcHxlbnwwfHx8fDE3NzI2MTMyOTh8MA&ixlib=rb-4.1.0&q=85',
      large: true,
    },
    {
      id: 'vitres',
      name: 'Nettoyage Vitres',
      description: 'Résultat sans traces garanti',
      price: 'Sur devis',
      icon: Sparkles,
      image: 'https://images.pexels.com/photos/713297/pexels-photo-713297.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    },
    {
      id: 'canape',
      name: 'Nettoyage Canapé',
      description: 'Élimination des taches et acariens',
      price: 'Sur devis',
      icon: HomeIcon,
      image: 'https://images.unsplash.com/photo-1686178827149-6d55c72d81df?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwyfHxjbGVhbmluZyUyMHNvZmElMjB1cGhvbHN0ZXJ5JTIwdmFjdXVtfGVufDB8fHx8MTc3MjYxMzI5N3ww&ixlib=rb-4.1.0&q=85',
    },
  ];

  const features = [
    {
      icon: MapPin,
      title: 'Service à domicile',
      description: 'Nous intervenons dans toute l\'Auvergne',
    },
    {
      icon: Shield,
      title: 'Produits professionnels',
      description: 'Utilisation de produits de qualité respectueux de vos biens',
    },
    {
      icon: Clock,
      title: 'Flexibilité horaire',
      description: 'Rendez-vous adaptés à votre emploi du temps',
    },
  ];

  const testimonials = [
    {
      name: 'Marie L.',
      location: 'Ambert',
      rating: 5,
      text: 'Excellent travail ! Ma voiture est comme neuve. Je recommande vivement BA NETTOYAGE.',
      service: 'Nettoyage Auto Complet',
    },
    {
      name: 'Pierre D.',
      location: 'Thiers',
      rating: 5,
      text: 'Très professionnel et ponctuel. Mon canapé a retrouvé ses couleurs d\'origine. Merci !',
      service: 'Nettoyage Canapé',
    },
    {
      name: 'Sophie M.',
      location: 'Clermont-Ferrand',
      rating: 5,
      text: 'Service impeccable, résultat au-delà de mes attentes. Prix très correct pour la qualité.',
      service: 'Nettoyage Vitres',
    },
  ];

  const stats = [
    { icon: Users, value: '200+', label: 'Clients satisfaits' },
    { icon: Car, value: '500+', label: 'Véhicules nettoyés' },
    { icon: Award, value: '5/5', label: 'Note moyenne' },
    { icon: MapPin, value: '100%', label: 'Auvergne couverte' },
  ];

  const beforeAfterImages = [
    {
      before: 'https://customer-assets.emergentagent.com/job_nettoyage-rdv/artifacts/s3cbflw5_1000017554.jpg',
      after: 'https://customer-assets.emergentagent.com/job_nettoyage-rdv/artifacts/92qhgfba_1000017555.jpg',
      title: 'Nettoyage Intérieur Véhicule',
    },
    {
      combined: 'https://customer-assets.emergentagent.com/job_nettoyage-rdv/artifacts/r9ol26ds_1000017559.jpg',
      title: 'Nettoyage Sièges Auto',
    },
  ];

  const faqs = [
    {
      question: 'Comment se passe une intervention à domicile ?',
      answer: 'Nous nous déplaçons directement chez vous avec tout le matériel nécessaire. Vous n\'avez rien à préparer, nous nous occupons de tout !',
    },
    {
      question: 'Quels sont vos délais d\'intervention ?',
      answer: 'Nous intervenons généralement sous 24 à 48h selon les disponibilités. Pour les urgences, contactez-nous directement par téléphone.',
    },
    {
      question: 'Quels produits utilisez-vous ?',
      answer: 'Nous utilisons uniquement des produits professionnels de haute qualité, respectueux de vos biens et de l\'environnement.',
    },
    {
      question: 'Proposez-vous des devis gratuits ?',
      answer: 'Oui, tous nos devis sont gratuits et sans engagement. Contactez-nous pour obtenir le vôtre !',
    },
    {
      question: 'Quelle est votre zone d\'intervention ?',
      answer: 'Nous intervenons dans toute l\'Auvergne : Puy-de-Dôme, Allier, Cantal, Haute-Loire.',
    },
  ];

  return (
    <div className="pt-16 md:pt-20" data-testid="home-page">
      {/* Pop-up Promotion */}
      <Dialog open={showPromo} onOpenChange={setShowPromo}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-2xl font-['Outfit']">
              <Gift className="w-6 h-6 text-amber-500" />
              Offre de bienvenue !
            </DialogTitle>
          </DialogHeader>
          <div className="text-center py-4">
            <div className="bg-gradient-to-r from-amber-500 to-amber-600 text-white text-4xl font-bold py-4 px-6 rounded-xl mb-4">
              -10%
            </div>
            <p className="text-slate-600 mb-4">
              Sur votre première réservation avec le code :
            </p>
            <div className="bg-slate-100 text-slate-900 font-mono font-bold text-xl py-3 px-6 rounded-lg mb-6">
              BIENVENUE10
            </div>
            <Link to="/booking" onClick={() => setShowPromo(false)}>
              <Button className="w-full rounded-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold">
                Réserver maintenant
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </DialogContent>
      </Dialog>

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1761312834150-4beefff097a7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwxfHxjYXIlMjB3YXNoaW5nJTIwZm9hbSUyMGV4dGVyaW9yJTIwc29hcHxlbnwwfHx8fDE3NzI2MTMyOTh8MA&ixlib=rb-4.1.0&q=85"
            alt="Nettoyage automobile professionnel"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 hero-gradient" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 mb-6">
              <MapPin className="w-5 h-5 text-sky-400" />
              <span className="text-sky-400 font-medium">Intervention dans toute l'Auvergne</span>
            </div>
            
            <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Votre véhicule et intérieur,{' '}
              <span className="text-sky-400">comme neufs</span>
            </h1>
            
            <p className="text-slate-300 text-lg md:text-xl mb-8 leading-relaxed">
              BA NETTOYAGE vous propose des services de nettoyage automobile à domicile et de nettoyage d'intérieur (vitres, canapés, fauteuils) de qualité professionnelle.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <Link to="/booking">
                <Button 
                  size="lg" 
                  className="w-full sm:w-auto rounded-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold px-8 py-6 text-lg btn-primary"
                  data-testid="cta-reserver"
                >
                  Réserver maintenant
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <a href="tel:0769119183">
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="w-full sm:w-auto rounded-full border-white/30 text-white hover:bg-white/10 px-8 py-6 text-lg"
                  data-testid="cta-appeler"
                >
                  <Phone className="mr-2 w-5 h-5" />
                  07 69 11 91 83
                </Button>
              </a>
            </div>

            <div className="flex flex-wrap gap-3">
              <span className="trust-badge !bg-white/10 !text-white backdrop-blur-sm">
                <CheckCircle2 className="w-4 h-4" />
                Devis gratuit
              </span>
              <span className="trust-badge !bg-white/10 !text-white backdrop-blur-sm">
                <CheckCircle2 className="w-4 h-4" />
                Service rapide
              </span>
              <span className="trust-badge !bg-white/10 !text-white backdrop-blur-sm">
                <CheckCircle2 className="w-4 h-4" />
                Satisfaction garantie
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-slate-900" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="w-12 h-12 rounded-full bg-sky-500/20 flex items-center justify-center mx-auto mb-3">
                  <stat.icon className="w-6 h-6 text-sky-400" />
                </div>
                <div className="text-3xl md:text-4xl font-bold text-white font-['Outfit'] mb-1">
                  {stat.value}
                </div>
                <div className="text-slate-400 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 md:py-32 bg-slate-50" data-testid="services-preview">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-['Outfit'] text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Nos services
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              Des prestations adaptées à vos besoins, réalisées avec soin et professionnalisme
            </p>
          </div>

          <div className="bento-grid">
            {services.map((service, index) => (
              <Link 
                key={service.id} 
                to={`/booking?service=${service.id}`}
                className={`service-card group relative overflow-hidden rounded-2xl bg-white border border-slate-100 hover:border-sky-200 ${
                  service.large ? 'bento-item-large' : ''
                }`}
                style={{ animationDelay: `${index * 0.1}s` }}
                data-testid={`service-card-${service.id}`}
              >
                <div className={`relative overflow-hidden ${service.large ? 'h-80 md:h-full' : 'h-48'}`}>
                  <img
                    src={service.image}
                    alt={service.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/20 to-transparent" />
                  
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <div className="flex items-center gap-2 mb-2">
                      <service.icon className="w-5 h-5 text-sky-400" />
                      <span className="text-sky-400 font-semibold text-sm">{service.price}</span>
                    </div>
                    <h3 className="font-['Outfit'] text-xl font-bold text-white mb-1">
                      {service.name}
                    </h3>
                    <p className="text-slate-300 text-sm">
                      {service.description}
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button variant="outline" size="lg" className="rounded-full px-8" data-testid="voir-tous-services">
                Voir tous nos services
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Before/After Gallery */}
      <section className="py-20 md:py-32" data-testid="before-after-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-['Outfit'] text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Nos résultats en images
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              Découvrez la qualité de notre travail à travers ces transformations
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {beforeAfterImages.map((item, index) => (
              <Card key={index} className="overflow-hidden border-none shadow-lg">
                <CardContent className="p-0">
                  {item.combined ? (
                    <div className="relative">
                      <img src={item.combined} alt="Avant / Après" className="w-full h-80 object-contain bg-slate-100" />
                    </div>
                  ) : (
                    <div className="grid grid-cols-2">
                      <div className="relative">
                        <img src={item.before} alt="Avant" className="w-full h-48 object-cover" />
                        <span className="absolute bottom-2 left-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded">
                          AVANT
                        </span>
                      </div>
                      <div className="relative">
                        <img src={item.after} alt="Après" className="w-full h-48 object-cover" />
                        <span className="absolute bottom-2 right-2 bg-green-500 text-white text-xs font-bold px-2 py-1 rounded">
                          APRÈS
                        </span>
                      </div>
                    </div>
                  )}
                  <div className="p-4 text-center">
                    <h3 className="font-semibold text-slate-900">{item.title}</h3>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 md:py-32 bg-slate-50" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-['Outfit'] text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Ce que disent nos clients
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              La satisfaction de nos clients est notre plus belle récompense
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <p className="text-slate-600 mb-6 italic">"{testimonial.text}"</p>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-slate-900">{testimonial.name}</p>
                      <p className="text-slate-500 text-sm">{testimonial.location}</p>
                    </div>
                    <span className="text-xs bg-sky-100 text-sky-600 px-2 py-1 rounded-full">
                      {testimonial.service}
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 md:py-32" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-['Outfit'] text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Pourquoi nous choisir ?
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              BA NETTOYAGE s'engage à vous offrir un service de qualité
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card 
                key={feature.title} 
                className="border-none shadow-none bg-slate-50 hover:bg-slate-100 transition-colors"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 rounded-2xl bg-sky-100 flex items-center justify-center mx-auto mb-6">
                    <feature.icon className="w-8 h-8 text-sky-500" />
                  </div>
                  <h3 className="font-['Outfit'] text-xl font-semibold text-slate-900 mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-slate-600">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 md:py-32 bg-slate-50" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-['Outfit'] text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Questions fréquentes
            </h2>
            <p className="text-slate-600 text-lg">
              Tout ce que vous devez savoir sur nos services
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`}
                className="bg-white rounded-xl border-none shadow-sm px-6"
              >
                <AccordionTrigger className="text-left font-medium text-slate-900 hover:no-underline py-5">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 pb-5">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="dark-section py-20 md:py-32" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-['Outfit'] text-3xl md:text-4xl font-bold text-white mb-6">
              Prêt à redonner de l'éclat à votre véhicule ou intérieur ?
            </h2>
            <p className="text-slate-400 text-lg mb-8">
              Réservez votre créneau en quelques clics ou appelez-nous directement pour un devis gratuit.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/booking">
                <Button 
                  size="lg" 
                  className="w-full sm:w-auto rounded-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold px-8 py-6 text-lg btn-primary"
                >
                  Réserver maintenant
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="w-full sm:w-auto rounded-full border-slate-600 text-white hover:bg-slate-800 px-8 py-6 text-lg"
                >
                  Nous contacter
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
